import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LEGAL_INFO } from "@/constants/legalInfo";

interface Props {
  open: boolean;
  onClose: () => void;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Stack>
  );
}

/** Provider details (Impressum), links to the legal pages and the build version. */
export function InfoDialog({ open, onClose }: Props) {
  const { t } = useTranslation();
  const version = __APP_COMMIT__ ? `${__APP_VERSION__} (${__APP_COMMIT__})` : __APP_VERSION__;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Typography variant="h6" component="span" sx={{ flex: 1 }}>
          {t("appInfo.title")}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label={t("common.close")}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">{t("appInfo.imprint")}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {LEGAL_INFO.company}
            </Typography>
            <Typography variant="body2">{LEGAL_INFO.street}</Typography>
            <Typography variant="body2">{LEGAL_INFO.city}</Typography>
            <Typography variant="body2">{LEGAL_INFO.country}</Typography>
          </Stack>

          <Stack spacing={0.5}>
            <Row label={t("appInfo.managingDirector")}>
              {t("appInfo.managingDirectorValue", {
                name: LEGAL_INFO.managingDirector,
                partner: LEGAL_INFO.generalPartner,
              })}
            </Row>
            <Row label={t("appInfo.phone")}>{LEGAL_INFO.phone}</Row>
            <Row label={t("appInfo.email")}>
              <Link href={`mailto:${LEGAL_INFO.email}`}>{LEGAL_INFO.email}</Link>
            </Row>
            <Row label={t("appInfo.register")}>{LEGAL_INFO.register}</Row>
            <Row label={t("appInfo.generalPartnerRegister")}>{LEGAL_INFO.generalPartnerRegister}</Row>
            <Row label={t("appInfo.vatId")}>{LEGAL_INFO.vatId}</Row>
            <Row label={t("appInfo.responsible")}>{LEGAL_INFO.managingDirector}</Row>
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Link href={LEGAL_INFO.imprintUrl} target="_blank" rel="noopener noreferrer">
              {t("appInfo.fullImprint")}
            </Link>
            <Link href={LEGAL_INFO.termsUrl} target="_blank" rel="noopener noreferrer">
              {t("appInfo.terms")}
            </Link>
            <Link href={LEGAL_INFO.privacyUrl} target="_blank" rel="noopener noreferrer">
              {t("appInfo.privacy")}
            </Link>
          </Stack>

          <Divider />

          <Stack spacing={0.5}>
            <Row label={t("appInfo.version")}>{version}</Row>
            <Row label={t("appInfo.buildDate")}>{format(new Date(__APP_BUILD_DATE__), "dd.MM.yyyy HH:mm")}</Row>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
